import 'leaflet/dist/leaflet.css';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Circle,
  CircleMarker,
  GeoJSON,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import { circleMarker, latLngBounds } from 'leaflet';
import type { PathOptions } from 'leaflet';
import { Crosshair, Minus, Plus } from 'lucide-react';
import { COHORT_PRESENTATION } from '../projectMapCohorts';
import { useAtlasLayer, type LayerKind, type ResourceLayer } from './atlasApi';
import {
  jobPoint,
  money,
  RISK_COLORS,
  STAGES,
  stageLabel,
  type BaseMap,
  type Job,
  type LayerId,
  type MapPoint,
} from './atlasModel';

export interface AtlasMapProps {
  jobs: Job[];
  selectedId: string | null;
  point: MapPoint | null;
  radius: number;
  basemap: BaseMap;
  layers: LayerId[];
  resourceData?: ResourceLayer;
  onSelect: (job: Job) => void;
  onPoint: (point: MapPoint) => void;
  onCluster: (jobs: Job[]) => void;
}
const EXTERNAL: LayerKind[] = [
  'demographics',
  'businesses',
  'development',
  'flood',
  'parcels',
  'access',
  'suppliers',
  'amenities',
];
const COLORS: Record<LayerKind, string> = {
  demographics: '#cfad50',
  businesses: '#83a9bf',
  development: '#e0b374',
  flood: '#6ca6cc',
  parcels: '#c8bea8',
  access: '#aab2ba',
  suppliers: '#9dc48e',
  amenities: '#ab96ba',
};
export function AtlasMap(props: AtlasMapProps) {
  const [tileError, setTileError] = useState(false);
  const [controlsHost, setControlsHost] = useState<HTMLDivElement | null>(null);
  useEffect(() => setTileError(false), [props.basemap]);
  const osm = props.basemap === 'dark' || props.basemap === 'street';
  const url = osm
    ? 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
    : 'https://server.arcgisonline.com/ArcGIS/rest/services/' +
      (props.basemap === 'satellite' ? 'World_Imagery' : 'World_Topo_Map') +
      '/MapServer/tile/{z}/{y}/{x}';
  return (
    <div className={'atlas-map atlas-map--' + props.basemap} aria-label="Interactive project map">
      <MapContainer
        center={[39.5, -98.35]}
        zoom={4}
        maxZoom={19}
        minZoom={2}
        zoomControl={false}
        scrollWheelZoom
      >
        <TileLayer
          key={props.basemap}
          url={url}
          maxZoom={19}
          attribution={
            osm
              ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              : 'Tiles &copy; Esri — Esri, Maxar, Earthstar Geographics and the GIS User Community'
          }
          eventHandlers={{ tileerror: () => setTileError(true) }}
        />
        <MapContents {...props} controlsHost={controlsHost} />
        {EXTERNAL.filter((kind) => props.layers.includes(kind)).map((kind) => (
          <ExternalLayer
            key={kind}
            kind={kind}
            point={props.point}
            radius={props.radius}
            controlsHost={controlsHost}
          />
        ))}
      </MapContainer>
      <div ref={setControlsHost} />
      <div className="atlas-map-caption">
        <span className="atlas-live-dot" />
        Recorded job locations <span>• Click the map to inspect a site</span>
      </div>
      {tileError ? (
        <p role="status" className="atlas-map-error">
          Basemap tiles are unavailable. Recorded job markers remain available; try another map
          appearance.
        </p>
      ) : null}
      <div className="atlas-map-legend" aria-label="Map legend">
        {(props.layers.includes('risk')
          ? [...Object.entries(RISK_COLORS), ['Unrecorded', '#929292']]
          : STAGES.filter((stage) => props.jobs.some((job) => job.cohort === stage)).map(
              (stage) => [COHORT_PRESENTATION[stage].label, COHORT_PRESENTATION[stage].color],
            )
        ).map(([label, color]) => (
          <span key={label}>
            <i style={{ background: color }} />
            {label}
          </span>
        ))}
        {props.layers.includes('resources') ? (
          <span>
            <i style={{ background: '#6ca6cc' }} />
            Dashed ring: recorded assignments
          </span>
        ) : null}
      </div>
    </div>
  );
}
function MapContents({
  jobs,
  selectedId,
  point,
  radius,
  layers,
  resourceData,
  onSelect,
  onPoint,
  onCluster,
  controlsHost,
}: AtlasMapProps & { controlsHost: HTMLDivElement | null }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const located = useMemo(() => jobs.filter((job) => jobPoint(job)), [jobs]);
  const assignedProjects = useMemo(
    () => new Set(resourceData?.projects.map((record) => record.projectId) ?? []),
    [resourceData],
  );
  useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
    click: (event) =>
      onPoint({
        latitude: event.latlng.lat,
        longitude: event.latlng.lng,
        label: 'Selected map point',
      }),
  });
  const fit = () => {
    if (located.length)
      map.fitBounds(
        latLngBounds(located.map((j) => [j.location.latitude!, j.location.longitude!])),
        { padding: [45, 55], maxZoom: 13, animate: false },
      );
  };
  // Refit only when the actual visible coordinates change, not on every source refresh.
  const locationKey = located
    .map((j) => j.id + ':' + j.location.latitude + ':' + j.location.longitude)
    .join('|');
  useEffect(() => {
    if (located.length)
      map.fitBounds(
        latLngBounds(located.map((j) => [j.location.latitude!, j.location.longitude!])),
        { padding: [45, 55], maxZoom: 13, animate: false },
      );
    // locationKey contains the complete coordinate set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, locationKey]);
  const latitude = point?.latitude,
    longitude = point?.longitude;
  useEffect(() => {
    if (latitude !== undefined && longitude !== undefined)
      map.setView([latitude, longitude], Math.max(map.getZoom(), 11), {
        animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      });
  }, [map, latitude, longitude]);
  useEffect(() => {
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);
  const groups = useMemo(() => {
    const bins = new Map<string, Job[]>();
    for (const job of located) {
      const pixel = map.project([job.location.latitude!, job.location.longitude!], zoom);
      const key = Math.floor(pixel.x / 38) + ':' + Math.floor(pixel.y / 38);
      bins.set(key, [...(bins.get(key) ?? []), job]);
    }
    return [...bins.values()];
  }, [located, map, zoom]);
  const clientCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const job of located)
      if (job.canonicalClientIdentityId)
        counts.set(
          job.canonicalClientIdentityId,
          (counts.get(job.canonicalClientIdentityId) ?? 0) + 1,
        );
    return counts;
  }, [located]);
  return (
    <>
      {layers.some((layer) => ['jobs', 'risk', 'value', 'clients', 'resources'].includes(layer))
        ? groups.map((group) => {
            const job = group[0]!;
            const latitude = group.reduce((s, j) => s + j.location.latitude!, 0) / group.length;
            const longitude = group.reduce((s, j) => s + j.location.longitude!, 0) / group.length;
            const clustered = group.length > 1;
            const selected = group.some((j) => j.id === selectedId);
            const color = layers.includes('risk')
              ? job.riskLevel
                ? RISK_COLORS[job.riskLevel]
                : '#929292'
              : COHORT_PRESENTATION[job.cohort].color;
            const amount = Number(job.financials?.contractValue);
            const clientSize = job.canonicalClientIdentityId
              ? (clientCounts.get(job.canonicalClientIdentityId) ?? 1)
              : 1;
            const size = clustered
              ? Math.min(22, 10 + Math.log2(group.length) * 2)
              : layers.includes('value') && job.financials && Number.isFinite(amount)
                ? Math.min(24, 5 + Math.sqrt(Math.max(0, amount) / 1_000_000) * 3)
                : layers.includes('clients')
                  ? Math.min(22, 5 + Math.sqrt(clientSize) * 3)
                  : selected
                    ? 9
                    : 6;
            return (
              <Fragment key={group.map((j) => j.id).join('|')}>
                {layers.includes('resources') &&
                group.some((j) => j.source === 'project' && assignedProjects.has(j.sourceId)) ? (
                  <CircleMarker
                    center={[latitude, longitude]}
                    radius={size + 5}
                    interactive={false}
                    pathOptions={{ color: '#6ca6cc', weight: 2, fill: false, dashArray: '2 3' }}
                  />
                ) : null}
                <CircleMarker
                  key={group.map((j) => j.id).join('|')}
                  center={[latitude, longitude]}
                  radius={size}
                  bubblingMouseEvents={false}
                  pathOptions={{
                    color: selected ? '#f2d89a' : clustered ? '#d5d5d5' : '#dedede',
                    weight: selected ? 3 : 1.3,
                    fillColor: clustered ? '#3c3c3c' : color,
                    fillOpacity: 0.92,
                  }}
                  eventHandlers={{
                    click: () => {
                      if (!clustered) onSelect(job);
                      else if (
                        zoom >= 16 ||
                        group.every(
                          (j) =>
                            j.location.latitude === job.location.latitude &&
                            j.location.longitude === job.location.longitude,
                        )
                      )
                        onCluster(group);
                      else
                        map.fitBounds(
                          latLngBounds(
                            group.map((j) => [j.location.latitude!, j.location.longitude!]),
                          ),
                          { padding: [70, 70], maxZoom: 17 },
                        );
                    },
                  }}
                >
                  <Tooltip
                    permanent={clustered}
                    direction={clustered ? 'center' : 'top'}
                    className={clustered ? 'atlas-cluster-count' : 'atlas-tooltip'}
                  >
                    {clustered ? (
                      group.length
                    ) : (
                      <>
                        <strong>{job.name}</strong>
                        <br />
                        {stageLabel(job.cohort)} · {money(job.financials?.contractValue)}
                      </>
                    )}
                  </Tooltip>
                </CircleMarker>
              </Fragment>
            );
          })
        : null}
      {point ? (
        <>
          {[1, 3, 5]
            .filter((mile) => mile <= radius)
            .map((mile) => (
              <Circle
                key={mile}
                center={[point.latitude, point.longitude]}
                radius={mile * 1609.344}
                interactive={false}
                pathOptions={{
                  color: '#cfad50',
                  weight: mile === radius ? 1.5 : 1,
                  dashArray: '5 6',
                  fillColor: '#cfad50',
                  fillOpacity: 0.015,
                }}
              />
            ))}
          <CircleMarker
            center={[point.latitude, point.longitude]}
            radius={5}
            interactive={false}
            pathOptions={{ color: '#f5e1b4', fillColor: '#cfad50', fillOpacity: 1, weight: 2 }}
          />
        </>
      ) : null}
      {controlsHost
        ? createPortal(
            <div className="atlas-map-controls">
              <button
                type="button"
                title="Fit visible jobs"
                aria-label="Fit visible jobs"
                onClick={(e) => {
                  e.stopPropagation();
                  fit();
                }}
              >
                <Crosshair size={16} />
              </button>
              <button
                type="button"
                title="Zoom in"
                aria-label="Zoom in"
                onClick={(e) => {
                  e.stopPropagation();
                  map.zoomIn();
                }}
              >
                <Plus size={16} />
              </button>
              <button
                type="button"
                title="Zoom out"
                aria-label="Zoom out"
                onClick={(e) => {
                  e.stopPropagation();
                  map.zoomOut();
                }}
              >
                <Minus size={16} />
              </button>
            </div>,
            controlsHost,
          )
        : null}
    </>
  );
}
function ExternalLayer({
  kind,
  point,
  radius,
  controlsHost,
}: {
  kind: LayerKind;
  point: MapPoint | null;
  radius: number;
  controlsHost: HTMLDivElement | null;
}) {
  const query = useAtlasLayer(kind, point, radius, true);
  const data = query.data;
  const color = COLORS[kind];
  const style = (feature?: GeoJSON.Feature): PathOptions => ({
    color,
    weight: 1,
    fillColor: kind === 'flood' && feature?.properties?.specialHazard ? '#679dc0' : color,
    fillOpacity: kind === 'flood' ? 0.23 : 0.08,
    dashArray: kind === 'businesses' ? '4 5' : undefined,
  });
  const state = !point
    ? 'Select a site to load'
    : query.isFetching
      ? 'Loading source…'
      : query.isError
        ? 'Source unavailable · click to retry'
        : data?.state === 'not_configured'
          ? 'Connection required'
          : data?.state === 'unavailable'
            ? 'Source unavailable · click to retry'
            : (data?.features.features.length ?? 0) +
              ' features · ' +
              (data?.state ?? 'unavailable');
  return (
    <>
      {controlsHost
        ? createPortal(
            <div className={'atlas-layer-status atlas-layer-status--' + kind}>
              <button
                type="button"
                onClick={() => void query.refetch()}
                disabled={!point || query.isFetching}
                title={data?.detail ?? state}
              >
                <i style={{ background: color }} />
                {kind === 'demographics'
                  ? 'Census tract'
                  : kind === 'businesses'
                    ? 'County'
                    : kind}{' '}
                <span>{state}</span>
              </button>
            </div>,
            controlsHost,
          )
        : null}
      {data?.features.features.length ? (
        <GeoJSON
          key={
            kind +
            ':' +
            data.retrievedAt +
            ':' +
            point?.latitude +
            ':' +
            point?.longitude +
            ':' +
            radius
          }
          data={data.features}
          style={style}
          attribution={data.attribution}
          pointToLayer={(_feature, latlng) =>
            circleMarker(latlng, { radius: 4, color, weight: 1.5, fillOpacity: 0.85 })
          }
          onEachFeature={(feature, layer) => {
            // Use DOM textContent rather than untrusted upstream HTML.
            const content = document.createElement('div');
            const title = document.createElement('strong');
            title.textContent = String(feature.properties?.label ?? data.source);
            const detail = document.createElement('div');
            detail.textContent = String(feature.properties?.detail ?? data.detail);
            content.append(title, detail);
            layer.bindTooltip(content);
          }}
        />
      ) : null}
    </>
  );
}
